import { markRaw } from 'vue'
import { defineStore } from 'pinia'
import { useUserStore } from '@/authentication/user.store'
import { useContentProfileStore } from '@/contentprofile/contentprofile.store'
import { useContentAccessStore } from '@/content_access/content_access.store'
import { useCustomizationDemoStore } from '@/customization-demo/customization-demo.store'
import { CUSTOMIZATIONS } from '@/customization-demo/customization_demo_catalog'
import type { LibraryGoal } from '@/library/library_types'
import { useVisibilityStore } from '@/visibility/visibility.store'
import { getStandardsForSet } from '@/standards/api'
import type { StandardDetail } from '@/standards/standard_types'
import { getStrategiesForUuids } from '@/strategies/api'
import type { ReferencedStrategy } from '@/strategies/strategy_types'
import { AccessPointsUtil, type AccessPointLevel, type AccessPointUuids } from '@/util/access_points'
import { ACTION_TYPES, CONTENT_TYPES, actionPermissions, blurContentPermissions } from '@/util/access_visibility'
import { isGoalPremium } from '@/util/goal_scores'
import { HTTPError } from '@/util/http'
import {
  getAnchorPage,
  getAnchorPageCustomization,
  getStaircaseGoals
} from './api'
import type {
  AnchorContentEventContext,
  AccessPointSummary,
  AnchorObjective,
  AnchorPageContent,
  AnchorPageCustomization,
  AnchorPageRequestParams,
  EditedObjective,
  SavedAnchorInfo,
  SavedAnchorPayload,
  SavedAnchorText,
  SavedAnchorUpdatePayload
} from './anchor_page_types'
import { useGeneratedAssessmentsStore } from './assessments/generated_assessments.store'
import { goalSectionsPayload, type GoalSection } from './goal_sections'
import type { StandardGroup } from './goal_standards'
import type { StaircaseGradeGroup } from './staircase_types'
import { useGoalResourcesStore } from './goal_resources.store'
import { useGoalTranslationStore } from './goal_translation.store'

type CustomObjective = Pick<AnchorObjective, 'level' | 'text' | 'title'>
type AccessPointsByLevel = Record<AccessPointLevel, AccessPointSummary | null>

export interface AnchorPageRequest {
  uuid: string
  isDraft: boolean
  /** A library copy of the page, whose text is shown in place of the source's. */
  savedCopy?: LibraryGoal
}

const FORBIDDEN_STATUS = 403
const NOT_FOUND_STATUS = 404
const PAGE_CACHE_LIFETIME_MS = 5 * 60 * 1000

interface CachedEdits {
  customPrimaryGoalText: string | null
  customGoalSections: GoalSection[] | null
  customObjectives: CustomObjective[]
}

// Vue 2's GET cache kept a page, and the edits made to it, for five minutes, so switching access
// point tabs or profiles and back found the same page with the teacher's edits still in it.
// Each showing gets its own copy, so a late customization for an earlier showing can't reach it.
interface CachedPage {
  content: AnchorPageContent
  cachedAt: number
  edits: CachedEdits | null
}

// access_visibility.js only checks reportError for truthiness, but infers a boolean from its default.
function quotaStatus() {
  const status = useContentAccessStore().contentAccessStatus
  const quotaStatusWithBooleanError = { ...status, reportError: !!status.reportError }
  return quotaStatusWithBooleanError
}

function emptyAccessPoints(): AccessPointsByLevel {
  return { grade_level: null, approaching: null, developing: null }
}

function accessPointsFor(content: AnchorPageContent): AccessPointsByLevel {
  const accessPoints = emptyAccessPoints()
  if (!content.access_points?.length) {
    return accessPoints
  }
  if (content.access_point_level) {
    accessPoints[content.access_point_level] = { ...content }
  }
  content.access_points
    .filter((accessPoint) => accessPoint.access_point_level)
    .forEach((accessPoint) => {
      accessPoints[accessPoint.access_point_level as AccessPointLevel] = accessPoint
    })
  return accessPoints
}

function copyObjective({ level, text, title }: CustomObjective): CustomObjective {
  return { level, text, title }
}

function copyGoalSections(sections: GoalSection[] | null): GoalSection[] | null {
  return sections?.map((section) => ({ ...section })) ?? null
}

function applyCustomization(content: AnchorPageContent, customization: AnchorPageCustomization) {
  if (customization.goal_text) {
    content.goal_text = customization.goal_text
  }
  if (customization.goal_sections?.length) {
    content.goal_sections = customization.goal_sections
    content.goal_format = customization.goal_format
  }
  customization.objectives?.forEach((customizedObjective) => {
    const objective = content.objectives?.find((candidate) => candidate.level === customizedObjective.level)
    if (objective && customizedObjective.text) {
      objective.text = customizedObjective.text
    }
  })
  if (customization.customized !== undefined) {
    content.customized = customization.customized
  }
}

function hasSavedObjectives(savedText: SavedAnchorText): boolean {
  return (savedText.objectives?.length ?? 0) > 0
}

// The saved copy's text becomes the page's own, so editing, resetting and unsaved changes are all measured against it.
// Its goal sections, and any objectives it saved, replace the source's outright, as in Vue 2, keeping only each
// objective level's source uuid and resources.
function applySavedText(content: AnchorPageContent, savedText: SavedAnchorText) {
  content.goal_text = savedText.grade_level_goal
  content.goal_sections = savedText.goal_sections ?? undefined
  content.goal_format = savedText.goal_format ?? undefined
  if (!hasSavedObjectives(savedText)) {
    return
  }
  const sourceObjectives = content.objectives ?? []
  content.objectives = (savedText.objectives ?? []).map(({ level, title, text }) => ({
    ...sourceObjectives.find((objective) => objective.level === level),
    level,
    title,
    text
  }))
}

// Vue 2 rendered a copy whose source failed with empty source content, which put its saved objectives in quarterly tabs.
function contentFromSavedCopyAlone(saved: LibraryGoal): AnchorPageContent {
  const objectives = (saved.objectives ?? []).map((objective) => ({ ...objective }))
  return {
    uuid: saved.goal_uuid ?? '',
    title: saved.title,
    plain_goal_text: '',
    super_page: true,
    subject_domains: [],
    goal_text: saved.grade_level_goal,
    goal_sections: saved.goal_sections ?? undefined,
    goal_format: saved.goal_format ?? undefined,
    objectives,
    objective_resources: Object.fromEntries(objectives.map((objective) => [objective.level, []]))
  }
}

function hasStatus(error: unknown, status: number): boolean {
  return error instanceof HTTPError && error.response.status === status
}

function requestParams({ isDraft }: AnchorPageRequest): AnchorPageRequestParams {
  const demoProfileId = useCustomizationDemoStore().demoProfileFor(CUSTOMIZATIONS.GOALS)
  const contentProfile = demoProfileId || useContentProfileStore().profileId
  const params: AnchorPageRequestParams = {
    with_access_points: 1,
    prefer_draft: isDraft ? 1 : 0,
    beta_mode: useVisibilityStore().betaVisibility
  }
  if (!contentProfile) {
    return params
  }
  // The base content renders first; the slower customization is fetched separately.
  return { ...params, content_profile: contentProfile, skip_customization: 'true' }
}

function pageCacheKey(uuid: string, params: AnchorPageRequestParams): string {
  return JSON.stringify({ uuid, ...params })
}

function isFresh(cachedPage: CachedPage | undefined): cachedPage is CachedPage {
  return !!cachedPage && Date.now() - cachedPage.cachedAt < PAGE_CACHE_LIFETIME_MS
}

function withoutStandard(groups: StandardGroup[], excluded: StandardDetail): StandardGroup[] {
  return groups
    .map((group) => {
      if (group.standardSetCode !== excluded.standard_set_code) {
        return group
      }
      const standards = group.standards.filter((standard) => standard.standard_long_ref !== excluded.standard_long_ref)
      return { ...group, standards }
    })
    .filter((group) => group.standards.length > 0)
}

export const useAnchorPageStore = defineStore('anchorPage', {
  state: () => ({
    content: null as AnchorPageContent | null,
    pageCache: {} as Record<string, CachedPage>,
    shownPageCacheKey: null as string | null,
    lastRequest: null as AnchorPageRequest | null,
    latestPageRequestNumber: 0,
    isFetching: false,
    isFetchingCustomization: false,
    customizationError: null as unknown,
    isForbidden: false,
    isNotFound: false,
    isShowingCopyWithoutSource: false,
    hasLoadError: false,
    isGoalCustomized: false,
    strategies: [] as ReferencedStrategy[],
    standardsBySetCode: {} as Record<string, StandardDetail[]>,
    staircaseContent: [] as StaircaseGradeGroup[],
    isFetchingStandards: false,
    isFetchingStaircase: false,
    accessPointsByLevel: emptyAccessPoints(),
    customAnchorUuid: null as string | null,
    customPrimaryGoalText: null as string | null,
    customGoalSections: null as GoalSection[] | null,
    customObjectives: [] as CustomObjective[],
    savedAnchor: null as SavedAnchorInfo | null,
    saveErrorMessage: '',
    isAutoReviewCompleted: false
  }),

  getters: {
    isShowing(state) {
      return (contentUuid: string | undefined): boolean => !!contentUuid && state.content?.uuid === contentUuid
    },
    isContentNotFound(state): boolean {
      const isUnpublishedForViewer =
        !state.isFetching && !!state.content?.uuid && !state.content.published && !useUserStore().isSuperAdmin
      return state.isNotFound || isUnpublishedForViewer
    },
    isOriginalPageAvailable(state): (sourceUuid: string | undefined) => boolean {
      return (sourceUuid) =>
        !!sourceUuid && state.content?.uuid === sourceUuid && !state.isShowingCopyWithoutSource && !this.isContentNotFound
    },
    isPremiumBlocked(state): boolean {
      const isContentPremium = !!state.content && isGoalPremium({ super_page: state.content.super_page, score: state.content.score })
      return state.isForbidden || (isContentPremium && !useUserStore().authInfo.isPremium)
    },
    strategyMap(state): Record<string, ReferencedStrategy> {
      const publishedStrategies = state.strategies.filter((strategy) => strategy.published === true)
      return Object.fromEntries(publishedStrategies.map((strategy) => [strategy.title.toLowerCase(), strategy]))
    },
    visibleGroupedStandardsBySetCode(state): StandardGroup[] {
      if (!state.content?.subject || !state.content.standard_set_refs) {
        return []
      }
      const contentProfileStore = useContentProfileStore()
      const visibleSetCodes = new Set(
        (state.content.subject_alignments ?? []).flatMap((alignment) =>
          contentProfileStore.visibleStandardSetsForSubject(alignment.subject, { onlyPublished: false }) as string[]
        )
      )
      return [...visibleSetCodes]
        .filter((setCode) => state.standardsBySetCode[setCode]?.length > 0)
        .map((setCode) => ({ standardSetCode: setCode, standards: state.standardsBySetCode[setCode] }))
    },
    keyStandard(): StandardDetail | undefined {
      const keyStandardSetCode = useContentProfileStore().keyStandardSetForSubject(this.content?.subject)
      const keyGroup = this.visibleGroupedStandardsBySetCode.find((group) => group.standardSetCode === keyStandardSetCode)
      const firstAlignedGroup = this.visibleGroupedStandardsBySetCode[0]
      return keyGroup?.standards[0] ?? firstAlignedGroup?.standards[0]
    },
    groupedSidebarStandards(): StandardGroup[] {
      if (!this.keyStandard) {
        return this.visibleGroupedStandardsBySetCode
      }
      return withoutStandard(this.visibleGroupedStandardsBySetCode, this.keyStandard)
    },
    accessInfo(state) {
      return blurContentPermissions(CONTENT_TYPES.SUPER_PAGE, state.content ?? {}, useUserStore().authInfo)
    },
    actionPermissions(state) {
      const content = state.content ?? {}
      const authInfo = useUserStore().authInfo
      return {
        save: actionPermissions(ACTION_TYPES.SAVE, CONTENT_TYPES.SUPER_PAGE, content, authInfo),
        printAssessment: actionPermissions(
          ACTION_TYPES.PRINT,
          CONTENT_TYPES.SUPER_PAGE_ASSESSMENT,
          content,
          authInfo,
          quotaStatus()
        ),
        printSavedAnchor: actionPermissions(
          ACTION_TYPES.PRINT,
          CONTENT_TYPES.SAVED_SUPER_PAGE,
          content,
          authInfo,
          quotaStatus()
        ),
        generateAssessment: actionPermissions(ACTION_TYPES.GENERATE_ASSESSMENT, CONTENT_TYPES.SAVED_SUPER_PAGE, content, authInfo),
        shareSavedGoal: actionPermissions(ACTION_TYPES.SHARE, CONTENT_TYPES.SAVED_GOAL, content, authInfo, quotaStatus())
      }
    },
    hasPMAs(state): boolean {
      return (state.content?.goal_resources?.length ?? 0) > 0
    },
    hasObjectives(state): boolean {
      return (state.content?.objectives?.length ?? 0) > 0
    },
    hasAccordionObjectives(state): boolean {
      const hasObjectiveResources = Object.keys(state.content?.objective_resources ?? {}).length > 0
      const hasAssessmentItems = Object.keys(state.content?.assessment_items ?? {}).length > 0
      return this.hasObjectives && !hasObjectiveResources && !hasAssessmentItems
    },
    showAnnualGoalFooter(state): boolean {
      const hasGoalExample = (state.content?.goal_example_text?.length ?? 0) > 0
      return !state.isFetching && (hasGoalExample || this.hasPMAs)
    },
    accessPointsLevelUuidMap(state): AccessPointUuids {
      return Object.fromEntries(
        AccessPointsUtil.orderedValues.map((level) => [level, state.accessPointsByLevel[level]?.uuid])
      )
    },
    areAccessPointsPublished(state): boolean {
      return AccessPointsUtil.orderedValues.every((level) => !!state.accessPointsByLevel[level]?.published)
    },
    contentEventContext(state): AnchorContentEventContext {
      const content = state.content
      return {
        goal_subject_alignments: content?.subject_alignments,
        goal_subject: content?.subject,
        goal_subject_domains: content?.subject_domains,
        goal_grades: content?.grades,
        goal_uuid: content?.uuid,
        goal_title: content?.title,
        goal_standards: content?.standard_set_refs?.map((ref) => ref.standard_long_ref),
        goal_author_id: content?.author_id,
        goal_is_anchor: content?.super_page,
        goal_has_objectives: this.hasObjectives,
        goal_objs_have_resources: this.hasObjectives && !this.hasAccordionObjectives,
        goal_is_library: false,
        goal_has_access_points: this.areAccessPointsPublished,
        goal_access_point_level: content?.access_point_level,
        goal_access_grade_level_uuid: content?.access_grade_level_uuid
      }
    },
    hasEditedText(state): boolean {
      const originalObjectives = state.content?.objectives ?? []
      const isObjectiveEdited = state.customObjectives.some((objective, index) => objective.text !== originalObjectives[index]?.text)
      return state.customPrimaryGoalText !== (state.content?.goal_text ?? null) || isObjectiveEdited
    },
    savedAnchorUpdatePayload(state): SavedAnchorUpdatePayload {
      return {
        goal_uuid: state.customAnchorUuid,
        user_uuid: useUserStore().userId,
        grade_level_goal: state.customPrimaryGoalText,
        objectives: state.customObjectives,
        ...goalSectionsPayload(state.customGoalSections, state.content?.goal_format)
      }
    },
    savedAnchorPayload(state): SavedAnchorPayload {
      return {
        goal_uuid: state.customAnchorUuid,
        user_uuid: useUserStore().userId,
        grade_level_goal: state.customPrimaryGoalText,
        objectives: state.customObjectives,
        content_profile_id: useContentProfileStore().profileId,
        ...goalSectionsPayload(state.customGoalSections, state.content?.goal_format)
      }
    }
  },

  actions: {
    async fetchAnchorPage(request: AnchorPageRequest, { isRetry = false, bypassCache = false } = {}): Promise<AnchorPageContent | undefined> {
      this.lastRequest = request
      const pageRequestNumber = ++this.latestPageRequestNumber
      const isSuperseded = () => pageRequestNumber !== this.latestPageRequestNumber
      const params = requestParams(request)
      const cacheKey = pageCacheKey(request.uuid, params)
      const cachedPage = this.pageCache[cacheKey]
      if (!bypassCache && !request.savedCopy && isFresh(cachedPage)) {
        return this.showCachedPage(cacheKey, cachedPage, params)
      }
      this.isFetching = true
      this.isForbidden = false
      this.isNotFound = false
      this.hasLoadError = false
      try {
        const content = await getAnchorPage(request.uuid, params)
        if (isSuperseded()) {
          return
        }
        if (request.savedCopy) {
          this.showSavedCopy(content, request.savedCopy, params)
          return content
        }
        this.pageCache[cacheKey] = markRaw({ content: structuredClone(content), cachedAt: Date.now(), edits: null })
        this.showContent(content, cacheKey)
        this.fetchCustomizationFor(content, params)
        return content
      } catch (error) {
        if (isSuperseded()) {
          return
        }
        this.isFetching = false
        const isPageForbidden = hasStatus(error, FORBIDDEN_STATUS)
        const isPageNotFound = hasStatus(error, NOT_FOUND_STATUS)
        if (!isPageForbidden && !isPageNotFound && !isRetry) {
          return this.fetchAnchorPage(request, { isRetry: true, bypassCache })
        }
        if (isPageNotFound) {
          delete this.pageCache[cacheKey]
        }
        if (request.savedCopy) {
          this.showSavedCopyWithoutSource(request.savedCopy)
          return
        }
        if (isPageNotFound || !this.isShowing(request.uuid)) {
          this.content = null
        }
        this.isForbidden = isPageForbidden
        this.isNotFound = isPageNotFound
        this.hasLoadError = !isPageForbidden && !isPageNotFound
        this.isFetchingCustomization = false
      }
    },

    async refetchCurrentPage() {
      if (!this.lastRequest) {
        return
      }
      await this.fetchAnchorPage(this.lastRequest, { bypassCache: true })
    },

    showCachedPage(cacheKey: string, cachedPage: CachedPage, params: AnchorPageRequestParams) {
      const content = structuredClone(cachedPage.content)
      this.showContent(content, cacheKey)
      if (cachedPage.edits) {
        this.customPrimaryGoalText = cachedPage.edits.customPrimaryGoalText
        this.customGoalSections = copyGoalSections(cachedPage.edits.customGoalSections)
        this.customObjectives = cachedPage.edits.customObjectives.map(copyObjective)
      }
      this.fetchCustomizationFor(content, params)
      return content
    },

    // A saved copy stays out of the page cache, so its text and edits never reach the source page's cached showing.
    showSavedCopy(content: AnchorPageContent, savedCopy: LibraryGoal, params: AnchorPageRequestParams) {
      applySavedText(content, savedCopy)
      this.showContent(content)
      // A saved copy's text overrides any customization, and a copy that saved no objectives shows the source's plain
      // ones, as Vue 2 did; auto-review still counts the goal as customized.
      this.isGoalCustomized = Boolean(content.should_customize && params.content_profile)
    },

    fetchCustomizationFor(content: AnchorPageContent, params: AnchorPageRequestParams) {
      if (content.should_customize && params.content_profile) {
        this.fetchCustomization(content.uuid, params.content_profile, params.beta_mode)
      }
    },

    showContent(content: AnchorPageContent, cacheKey: string | null = null) {
      this.content = content
      this.shownPageCacheKey = cacheKey
      this.isForbidden = false
      this.isNotFound = false
      this.isShowingCopyWithoutSource = false
      this.hasLoadError = false
      this.customizationError = null
      this.strategies = []
      this.standardsBySetCode = {}
      this.staircaseContent = []
      this.isFetchingStandards = false
      this.isFetchingStaircase = false
      this.resetSaveStatus()
      this.isGoalCustomized = false
      this.isAutoReviewCompleted = false
      this.isFetchingCustomization = false
      this.accessPointsByLevel = accessPointsFor(content)
      this.initializeCustomAnchorPage()
      this.isFetching = false
      this.fetchStrategies(content.adaptation_strategies ?? [])
      this.fetchStandardsAndStaircase()
    },

    async fetchCustomization(uuid: string, contentProfile: string, isBetaMode: boolean) {
      const requestedContent = this.content
      const isStillShowingRequestedContent = () => this.content === requestedContent && this.isShowing(uuid)
      this.isGoalCustomized = true
      this.isFetchingCustomization = true
      this.customizationError = null
      try {
        const customization = await getAnchorPageCustomization(uuid, contentProfile, isBetaMode)
        if (requestedContent && isStillShowingRequestedContent()) {
          applyCustomization(requestedContent, customization)
          this.initializeCustomAnchorPage()
        }
      } catch (error) {
        console.error('Failed to fetch customization:', error)
        if (isStillShowingRequestedContent()) {
          this.customizationError = error
        }
      } finally {
        if (isStillShowingRequestedContent()) {
          this.isFetchingCustomization = false
        }
      }
    },

    async fetchStrategies(uuids: string[]) {
      const contentUuid = this.content?.uuid
      try {
        const strategies = await getStrategiesForUuids(uuids)
        if (this.isShowing(contentUuid)) {
          this.strategies = strategies
        }
      } catch (error) {
        console.error('Failed to fetch referenced strategies:', error)
      }
    },

    async fetchStandardsAndStaircase() {
      const profileId = useContentProfileStore().profileId
      if (!this.content?.uuid || !profileId) {
        return
      }
      await Promise.all([this.fetchStandards(), this.fetchStaircase()])
    },

    async fetchStandards() {
      const visibleStandardSets = useContentProfileStore().visibleStandardSets as string[]
      const standardSetRefs = this.content?.standard_set_refs ?? []
      if (!visibleStandardSets.length) {
        return
      }
      const contentUuid = this.content?.uuid
      this.isFetchingStandards = true
      const setsWithRefs = visibleStandardSets
        .map((setCode) => ({
          setCode,
          longRefs: standardSetRefs.filter((ref) => ref.standard_set_code === setCode).map((ref) => ref.standard_long_ref)
        }))
        .filter(({ longRefs }) => longRefs.length > 0)
      try {
        await Promise.all(setsWithRefs.map(async ({ setCode, longRefs }) => {
          const standards = await getStandardsForSet(setCode, longRefs)
          if (this.isShowing(contentUuid)) {
            this.standardsBySetCode[setCode] = standards.filter((standard): standard is StandardDetail => !!standard)
          }
        }))
      } catch (error) {
        console.error('Failed to fetch standards:', error)
      } finally {
        if (this.isShowing(contentUuid)) {
          this.isFetchingStandards = false
        }
      }
    },

    async fetchStaircase() {
      const subject = this.content?.subject
      const contentUuid = this.content?.uuid
      if (!subject || !contentUuid) {
        return
      }
      const staircaseStandardSetCode = useContentProfileStore().staircaseStandardSetForSubject(subject)
      if (!staircaseStandardSetCode) {
        return
      }
      this.isFetchingStaircase = true
      try {
        const staircaseContent = await getStaircaseGoals(contentUuid, staircaseStandardSetCode, subject)
        if (this.isShowing(contentUuid)) {
          this.staircaseContent = staircaseContent
        }
      } catch (error) {
        console.error('Failed to fetch standard staircase:', error)
      } finally {
        if (this.isShowing(contentUuid)) {
          this.isFetchingStaircase = false
        }
      }
    },

    async fetchAllResources() {
      const content = this.content
      if (!content) {
        return
      }
      const resourcesStore = useGoalResourcesStore()
      if (content.goal_resources?.length) {
        await resourcesStore.fetchResources(content.uuid, content.goal_resources)
      }
      const objectivesWithResources = (content.objectives ?? []).filter((objective) => objective.uuid && objective.resources?.length)
      await Promise.all(
        objectivesWithResources.map((objective) => resourcesStore.fetchResources(objective.uuid as string, objective.resources as string[]))
      )
    },

    initializeCustomAnchorPage() {
      this.customAnchorUuid = this.content?.uuid ?? null
      this.customPrimaryGoalText = this.content?.goal_text ?? null
      this.customGoalSections = this.content?.goal_sections ?? null
      this.customObjectives = (this.content?.objectives ?? []).map(copyObjective)
    },

    /**
     * For a library copy whose source anchor page would not load, as for a signed-out visitor to a share link.
     * It supersedes any request in flight and becomes the last request, so a refetch that fails again lands here too.
     */
    showSavedCopyWithoutSource(savedCopy: LibraryGoal) {
      this.latestPageRequestNumber++
      this.lastRequest = { uuid: savedCopy.goal_uuid ?? '', isDraft: false, savedCopy }
      this.showContent(contentFromSavedCopyAlone(savedCopy))
      this.isShowingCopyWithoutSource = true
    },

    /**
     * After a save, the copy's new text is the page's own again, with nothing left unsaved. A response for another
     * copy is ignored, and a refetch still carrying the old text is superseded. Returns whether the text was applied.
     */
    showSavedText(savedCopy: LibraryGoal): boolean {
      const shownCopy = this.lastRequest?.savedCopy
      if (!this.content || !this.lastRequest || savedCopy.uuid !== shownCopy?.uuid) {
        return false
      }
      this.latestPageRequestNumber++
      this.isFetching = false
      this.lastRequest = { ...this.lastRequest, savedCopy }
      const content = { ...this.content }
      applySavedText(content, savedCopy)
      this.content = content
      this.initializeCustomAnchorPage()
      return true
    },

    /** Puts the shown copy's saved text back, dropping every unsaved edit. */
    discardEdits() {
      this.initializeCustomAnchorPage()
    },

    setPrimaryGoalText(text: string) {
      this.customPrimaryGoalText = text
      this.keepEditsInCache()
    },

    setGoalSections(sections: GoalSection[]) {
      this.customGoalSections = sections
      this.keepEditsInCache()
    },

    setObjective({ index, objective }: EditedObjective) {
      this.customObjectives[index] = copyObjective(objective)
      this.keepEditsInCache()
    },

    keepEditsInCache() {
      const cachedPage = this.shownPageCacheKey ? this.pageCache[this.shownPageCacheKey] : undefined
      if (!cachedPage) {
        return
      }
      cachedPage.edits = {
        customPrimaryGoalText: this.customPrimaryGoalText,
        customGoalSections: copyGoalSections(this.customGoalSections),
        customObjectives: this.customObjectives.map(copyObjective)
      }
    },

    recordSaveSuccess(savedAnchor: SavedAnchorInfo) {
      this.savedAnchor = savedAnchor
    },

    recordSaveError(message: string) {
      this.saveErrorMessage = message
    },

    resetSaveStatus() {
      this.savedAnchor = null
      this.saveErrorMessage = ''
    },

    setAutoReviewCompleted(isCompleted: boolean) {
      this.isAutoReviewCompleted = isCompleted
    },

    // Leaving supersedes any request still in flight, and the count survives the reset, so a late
    // answer for the page being left can neither refill the store nor pass as the next page's latest.
    clearPage() {
      const supersedingRequestNumber = this.latestPageRequestNumber + 1
      const pageCache = this.pageCache
      this.$reset()
      this.latestPageRequestNumber = supersedingRequestNumber
      this.pageCache = pageCache
      useGoalResourcesStore().$reset()
      useGeneratedAssessmentsStore().$reset()
      useGoalTranslationStore().deactivateAllTranslations()
    }
  }
})
